import { presenceRef } from '@/utils/firebase'
import { Button, ButtonVariant, Container, ContainerHeader, Table } from '@Core/Components'
import { Loader } from '@Core/Components/Loader/Loader'
import { LinkStatus, ManagedUser } from '@User/Data/DataSource/API/Entity/Identity'
import { NewUserModal } from '@User/Common/NewUserModal/NewUserModal'
import { useManagedUsers } from '@User/Hooks/useManagedUsers/useManagedUsers'
import { RoutesPaths } from '@User/RoutesPaths'
import { roles } from '@User/roles'
import { formatRouteId } from '@Utils/Formatters/formatRouteId'
import { format } from 'date-fns'
import {
    limitToLast,
    onValue,
    orderByValue,
    query,
} from 'firebase/database'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormControl, MenuItem, Select } from '@material-ui/core'
import { RightContent } from '@Entry/Common/Header/Header.styles'
import { OnlineTag } from './UserList.styles'

type UserStatusFilter = 'active' | 'disabled'
type LinkFilter = 'all' | LinkStatus

export const LINK_STATUS_LABELS: Record<LinkStatus, string> = {
    linked: 'Vinculado à Microsoft',
    pending: 'Pendente',
    'password-exception': 'Exceção (senha autorizada)',
}

const PROVIDER_LABELS: Record<string, string> = {
    'microsoft.com': 'Microsoft',
    password: 'Senha',
}

const lastLoginOf = (user: ManagedUser) => {
    const at = user.lastLogin?.at ?? user.metadata?.lastSignInTime
    if (!at) return 'N/D'
    const when = format(new Date(at), 'dd/MM/yyyy HH:mm')
    const provider = user.lastLogin?.provider
    return provider ? `${when} · ${PROVIDER_LABELS[provider] ?? provider}` : when
}

export const UserList = () => {
    const { users, loading, reloadUsers } = useManagedUsers()
    const navigate = useNavigate()
    const [isCreateUserOpen, setIsCreateUserOpen] = useState(false)
    const [onlineUsers, setOnlineUsers] = useState<Record<string, boolean>>({})
    const [statusFilter, setStatusFilter] = useState<UserStatusFilter>('active')
    const [linkFilter, setLinkFilter] = useState<LinkFilter>('all')

    const isUserDisabled = (user: ManagedUser) => user.firebase?.disabled === true

    const filteredUsers = useMemo(() => {
        return users.filter(
            (user) =>
                (statusFilter === 'active'
                    ? !isUserDisabled(user)
                    : isUserDisabled(user)) &&
                (linkFilter === 'all' || user.linkStatus === linkFilter)
        )
    }, [linkFilter, statusFilter, users])

    const handleNavigate = (uid: string) => () => {
        navigate(formatRouteId(RoutesPaths.UserDetail, uid))
    }

    const handleOpenCreateUser = () => {
        setIsCreateUserOpen(true)
    }

    const handleStatusFilterChange = (
        event: ChangeEvent<{ value: unknown }>
    ) => {
        setStatusFilter(event.target.value as UserStatusFilter)
    }

    const handleLinkFilterChange = (event: ChangeEvent<{ value: unknown }>) => {
        setLinkFilter(event.target.value as LinkFilter)
    }

    const isOnline = useCallback(
        (id: string) => {
            if (!id) return false
            return onlineUsers[id] === true
        },
        [onlineUsers]
    )

    useEffect(() => {
        const onlineUsersQuery = query(
            presenceRef,
            orderByValue(),
            limitToLast(10)
        )
        const unsubscribe = onValue(onlineUsersQuery, (snapshot) => {
            const onlineUsers = snapshot.val()
            setOnlineUsers(onlineUsers ?? {})
        })

        return unsubscribe
    }, [])

    if (loading) return <Loader />

    return (
        <Container>
                <ContainerHeader>
                    <h1 className="text-2xl font-bold">Usuários</h1>
                    <RightContent>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">Status</span>
                            <FormControl variant="outlined" size="small">
                                <Select
                                    data-testid="user-status-filter"
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                >
                                    <MenuItem value="active">Ativos</MenuItem>
                                    <MenuItem value="disabled">
                                        Desativados
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">Login</span>
                            <FormControl variant="outlined" size="small">
                                <Select
                                    data-testid="user-link-filter"
                                    value={linkFilter}
                                    onChange={handleLinkFilterChange}
                                >
                                    <MenuItem value="all">Todos</MenuItem>
                                    <MenuItem value="pending">Pendentes</MenuItem>
                                    <MenuItem value="linked">Vinculados</MenuItem>
                                    <MenuItem value="password-exception">
                                        Exceções
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <Button
                            onClick={handleOpenCreateUser}
                            variant={ButtonVariant.primary}
                        >
                            Novo Usuário
                        </Button>
                    </RightContent>
                </ContainerHeader>
                <Table>
                    <thead>
                        <tr>
                            <th></th>
                            <th>Nome</th>
                            <th>E-mail</th>
                            <th>Status</th>
                            <th>Login</th>
                            <th>Ultimo Login</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan={6}>
                                    Nenhum usuário{' '}
                                    {statusFilter === 'active'
                                        ? 'ativo'
                                        : 'desativado'}
                                    encontrado
                                </td>
                            </tr>
                        ) : (
                            filteredUsers.map((user: ManagedUser) => {
                                return (
                                    <tr
                                        className="clickable"
                                        key={user._id}
                                        onClick={handleNavigate(user.mail)}
                                    >
                                        <td>
                                            <OnlineTag
                                                isOnline={isOnline(user._id)}
                                                variant={
                                                    isOnline(user._id)
                                                        ? 'default'
                                                        : 'outlined'
                                                }
                                                size="small"
                                                label={
                                                    isOnline(user._id)
                                                        ? 'Online'
                                                        : 'Offline'
                                                }
                                            />
                                        </td>
                                        <td>{user.name || user?.displayName}</td>
                                        <td>{user.mail}</td>
                                        <td>
                                            {isUserDisabled(user)
                                                ? 'Desativado'
                                                : 'Ativo'}
                                        </td>
                                        <td data-testid="user-link-status">
                                            {LINK_STATUS_LABELS[user.linkStatus] ??
                                                LINK_STATUS_LABELS.pending}
                                        </td>
                                        <td>{lastLoginOf(user)}</td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </Table>
                <NewUserModal
                    isOpen={isCreateUserOpen}
                    setIsOpen={setIsCreateUserOpen}
                    callback={reloadUsers}
                />
        </Container>
    )
}
